import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Base_URL, ApiCall } from "../Common/Path";
import customFailAlert from "../Alerts/customFailAlert";

const RenameHouseholdModal = ({
  open,
  onClose,
  household,
  onHouseholdRenamed,
}) => {
  const [householdName, setHouseholdName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize with current household name when modal opens
  useEffect(() => {
    if (open && household) {
      setHouseholdName(household.householdName || "");
    }
  }, [open, household]);

  const handleClose = () => {
    setHouseholdName("");
    setIsUpdating(false);
    onClose();
  };

  const handleUpdate = async () => {
    if (!household || !householdName.trim() || isUpdating) return;

    try {
      setIsUpdating(true);

      // Get all existing members
      const existingMembers = Array.isArray(household.members)
        ? household.members
        : [];

      const membersPayload = existingMembers.map((m) => ({
        userRoleId: m?.userRole?.id,
        permissionGroupId: m?.permissionGroup?.id || null,
        isOwner: !!m?.isOwner,
      }));

      const body = {
        householdName: householdName.trim(),
        description: household.description || "",
        members: membersPayload,
        invitationExpiryDays: 7,
        isActive:
          typeof household.isActive === "boolean" ? household.isActive : true,
      };

      const url = `${Base_URL}/households/${household.id}`;
      const response = await ApiCall(url, body, "put");
      const statusCode = response?.statusCode || response?.code;

      const ok =
        statusCode === 200 ||
        statusCode === 201 ||
        String(response?.status || "").toUpperCase() === "SUCCESS";

      if (!ok) {
        const errorMessage =
          response?.message ||
          "Unable to rename household. Please try again.";
        customFailAlert(errorMessage);
        if (onHouseholdRenamed) {
          onHouseholdRenamed({ success: false, message: errorMessage });
        }
        return;
      }

      if (onHouseholdRenamed) {
        onHouseholdRenamed({
          success: true,
          message: "Household renamed successfully",
          data: response,
        });
      }

      handleClose();
    } catch (error) {
      console.error("Error renaming household:", error);
      const errorMessage = "Something went wrong while renaming the household.";
      customFailAlert(errorMessage);
      if (onHouseholdRenamed) {
        onHouseholdRenamed({ success: false, message: errorMessage });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const isNameValid = householdName.trim().length > 0;
  const isNameChanged =
    householdName.trim() !== (household?.householdName || "").trim();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
            >
              Rename Household
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Enter a new name for this household.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "#6B7280", mt: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <TextField
          fullWidth
          label="Household Name"
          placeholder="Enter name of the household"
          value={householdName}
          onChange={(e) => setHouseholdName(e.target.value)}
          disabled={isUpdating}
          autoFocus
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              "& fieldset": {
                borderColor: "#2563EB",
              },
              "&:hover fieldset": {
                borderColor: "#2563EB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2563EB",
              },
            },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isUpdating}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            borderColor: "#D1D5DB",
            color: "#374151",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={!isNameValid || !isNameChanged || isUpdating}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            backgroundColor:
              isNameValid && isNameChanged && !isUpdating
                ? "#2563EB"
                : "#E5E7EB",
            color:
              isNameValid && isNameChanged && !isUpdating
                ? "#FFFFFF"
                : "#9CA3AF",
            fontWeight: 600,
            "&:hover": {
              backgroundColor:
                isNameValid && isNameChanged && !isUpdating
                  ? "#1D4ED8"
                  : "#E5E7EB",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isUpdating ? "Updating..." : "Update Name"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameHouseholdModal;

